import { useEffect, useRef } from 'react'
import { useAuth0 } from '@auth0/auth0-react'
import { useDispatch, useSelector } from 'react-redux'
import { setSessionNonce } from '../../actions/Login'

// Bridges @auth0/auth0-react to the Redux session (PD-27768):
//  - on authentication, pushes the Auth0 access token into login.session.nonce
//    so client.js sends it as `Authorization: Bearer` (Howie then fetches
//    /sessions/me for the user + configuration).
//  - when the local session is cleared (logout), tears down the Auth0 session.
// Rendered only inside Auth0Provider (flag on), so useAuth0 is always available.
export default function Auth0Session() {
  const { isAuthenticated, isLoading, getAccessTokenSilently, logout } = useAuth0()
  const dispatch = useDispatch()
  const nonce = useSelector((state) => state.login.session.nonce)
  const tokenSet = useRef(false)

  useEffect(() => {
    if (isLoading || !isAuthenticated) return undefined

    let active = true
    getAccessTokenSilently()
      .then((token) => {
        if (!active) return
        tokenSet.current = true
        dispatch(setSessionNonce(token))
      })
      .catch(() => {
        // Silent refresh failed (e.g. Auth0 rejects the refresh token — 403 on
        // /oauth/token). The SDK's cached isAuthenticated stays true (stale
        // localStorage session), so Auth0AutoRedirect's `if (isAuthenticated)
        // return` never fires and the app hangs with no nonce. Force a real
        // Auth0 logout to clear that stale state so the redirect-to-login path
        // (Auth0AutoRedirect) actually engages on the next render.
        if (!active) return
        logout({ logoutParams: { returnTo: window.location.origin } })
      })

    return () => { active = false }
  }, [isAuthenticated, isLoading, getAccessTokenSilently, logout, dispatch])

  useEffect(() => {
    // Local session cleared after we'd authenticated (logout) → end the Auth0
    // session too. `tokenSet` guards the brief authenticated-but-not-yet-synced
    // window so we don't log out before the token is first stored.
    if (!isLoading && isAuthenticated && tokenSet.current && !nonce) {
      logout({ logoutParams: { returnTo: window.location.origin } })
    }
  }, [nonce, isAuthenticated, isLoading, logout])

  return null
}
